import { useEffect, useState } from "react";
import { MapPin, Stamp, Building2, Globe2, Compass, Sun, ArrowUpRight, Landmark, Navigation2, PlaneTakeoff, Route as RouteIcon, Mountain } from "lucide-react";
import api from "../../lib/api";

/* EssenceCard — editorial "essence-of-place" summary block for country / city / airport pages.
   Warm cream canvas + mustard-gold accents + big serif — inspired by luxury
   travel-agency editorial style but powered by our own data.

   Shapes:
     <EssenceCard kind="country" code="CH" name="Switzerland" />
     <EssenceCard kind="city"    city={cityObj} />              // cityObj from /api/cities/{name}
     <EssenceCard kind="airport" airport={airportObj} />        // airportObj from /api/airports/{code}
*/
export default function EssenceCard({ kind = "country", code, name, city, airport }) {
  const [country, setCountry] = useState(null);
  const [visa, setVisa] = useState(null);

  const iso =
    kind === "country" ? code :
    kind === "city"    ? (city?.iso2 || city?.country_code) :
                         (airport?.iso_country);
  const label =
    kind === "country" ? name :
    kind === "city"    ? city?.name :
                         (airport?.name || airport?.iata_code);

  useEffect(() => {
    let live = true;
    if (kind === "country" && code) {
      api.get(`/countries/${code}`).then((r) => { if (live) setCountry(r.data); }).catch(() => {});
    }
    if (iso) {
      api.get(`/visas/countries/${iso}`).then((r) => { if (live) setVisa(r.data); }).catch(() => {});
    }
    return () => { live = false; };
  }, [kind, code, iso]);

  const rows = [];

  if (kind === "country") {
    if (visa?.region || country?.continent) rows.push({ Icon: Globe2, label: "Region", value: [visa?.region, country?.continent].filter(Boolean).join(" · ") });
    if (visa?.capital) rows.push({ Icon: MapPin, label: "Capital", value: visa.capital });
    if (visa?.entry_programs?.length || visa?.summary) {
      const programs = (visa.entry_programs || []).slice(0, 2).join(", ");
      rows.push({ Icon: Stamp, label: "Visa & Entry", value: programs || (visa.summary || "").slice(0, 90), link: visa?.links?.visa_info });
    }
    if (country?.airport_count || (country?.airports || []).length) {
      const count = country.airport_count || country.airports.length;
      const codes = (country.airports || []).map((a) => a.iata_code).filter(Boolean).slice(0, 3).join(" · ");
      rows.push({ Icon: Building2, label: "Air Access", value: `${count} airports${codes ? ` · Major: ${codes}` : ""}` });
    }
  } else if (kind === "city" && city) {
    if (city.country) rows.push({ Icon: Globe2, label: "Country", value: [city.country, city.iso2].filter(Boolean).join(" · ") });
    if (city.state_name || city.region) rows.push({ Icon: Landmark, label: "State / Region", value: city.state_name || city.region });
    if (visa?.entry_programs?.length || visa?.summary) {
      const programs = (visa.entry_programs || []).slice(0, 2).join(", ");
      rows.push({ Icon: Stamp, label: "Visa & Entry", value: programs || (visa.summary || "").slice(0, 90), link: visa?.links?.visa_info });
    }
    if (city.lat != null && city.lon != null) {
      const fmt = (n) => Number(n).toFixed(3);
      rows.push({ Icon: Navigation2, label: "Coordinates", value: `${fmt(city.lat)}°, ${fmt(city.lon)}°` });
    }
  } else if (kind === "airport" && airport) {
    if (airport.municipality || airport.country_name) {
      const bits = [airport.municipality, airport.iso_region?.replace(/^[A-Z]{2}-/, ""), airport.country_name].filter(Boolean);
      rows.push({ Icon: MapPin, label: "Location", value: bits.join(" · ") });
    }
    if (airport.routes_count || airport.destinations_count) {
      rows.push({ Icon: RouteIcon, label: "Connectivity", value: `${airport.routes_count || 0} routes · ${airport.destinations_count || 0} destinations` });
    }
    if (airport.elevation_ft != null || airport.latitude != null) {
      const parts = [];
      if (airport.latitude != null) parts.push(`${Number(airport.latitude).toFixed(3)}°, ${Number(airport.longitude).toFixed(3)}°`);
      if (airport.elevation_ft) parts.push(`${airport.elevation_ft} ft elev.`);
      rows.push({ Icon: Mountain, label: "Position", value: parts.join(" · ") });
    }
    if (visa?.entry_programs?.length || visa?.summary) {
      const programs = (visa.entry_programs || []).slice(0, 2).join(", ");
      rows.push({ Icon: Stamp, label: "Visa & Entry", value: programs || (visa.summary || "").slice(0, 90), link: visa?.links?.visa_info });
    }
  }

  if (rows.length < 3) return null;

  const eyebrow =
    kind === "country" ? "Overview" :
    kind === "city"    ? "The city in a glance" :
                         "The gateway in a glance";
  const heading = "The Essence of";
  const summary = kind === "country" ? visa?.summary : null;
  const badge = kind === "airport" ? airport?.iata_code : null;

  return (
    <section className="max-w-4xl mx-auto px-5 lg:px-8 py-10 -mt-6 relative z-10" data-testid={`essence-card-${kind}`}>
      <div className="rounded-2xl shadow-lg border border-amber-100/80 overflow-hidden"
           style={{ background: "linear-gradient(180deg, #faf7f0 0%, #ffffff 100%)" }}>
        <div className="p-6 sm:p-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.32em] text-amber-700 inline-flex items-center gap-1.5">
                <Compass className="w-3.5 h-3.5" /> {eyebrow}
              </div>
              <h2 className="mt-2 font-serif font-800 text-3xl sm:text-4xl text-slate-900 leading-[1.1] tracking-tight">
                {heading} <span className="italic text-amber-700">{label}</span>
              </h2>
            </div>
            {badge && (
              <div className="shrink-0 px-3 py-1.5 border-2 border-amber-500 rounded-lg text-amber-800 font-mono font-800 text-lg tracking-widest bg-amber-50">
                {badge}
              </div>
            )}
          </div>
          {summary && <p className="mt-3 text-[15px] text-slate-700 leading-relaxed max-w-2xl">{summary}</p>}

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
            {rows.map(({ Icon, label: lbl, value, link }) => (
              <div key={lbl} className="flex items-start gap-3" data-testid={`essence-row-${lbl.toLowerCase().replace(/\s|\/|&/g, "-")}`}>
                <span className="w-9 h-9 rounded-full bg-amber-50 border border-amber-200 grid place-items-center shrink-0">
                  <Icon className="w-4 h-4 text-amber-700" strokeWidth={2.2} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-slate-500">{lbl}</div>
                  <div className="mt-0.5 text-sm text-slate-900 font-600 leading-snug">
                    {value}
                    {link && (
                      <a href={link} target="_blank" rel="noopener noreferrer"
                         className="inline-flex items-center gap-0.5 ml-1 text-amber-700 hover:underline text-xs font-700">
                        Official <ArrowUpRight className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {visa?.confidence && kind === "country" && (
            <div className="mt-6 pt-4 border-t border-amber-100 flex items-center gap-2 text-[11px] text-slate-500">
              <Sun className="w-3.5 h-3.5 text-amber-500" />
              Data confidence <span className="font-mono font-700 text-amber-700">{visa.confidence}</span>
              {visa.last_verified && <>· verified {visa.last_verified}</>}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
